<template>
  <Modal
    :isOpen="isOpen"
    @update:isOpen="$emit('update:isOpen', $event)"
    :modalId="`edit-reefer-details-${container?.id}`"
    subtitle="Genset, temperature and vents settings for this reefer, then save."
    maxWidth="2xl"
  >
    <template #title>Edit reefer details</template>
    <template #content>
      <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4 tw-text-left">
        <label class="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-slate-700 sm:tw-col-span-2">
          <input
            v-model="editingReeferDetails.isGensetRequired"
            type="checkbox"
            class="tw-h-4 tw-w-4 tw-rounded tw-border-slate-300"
            :disabled="isSaving"
          />
          Genset needed
        </label>

        <FilterableDropdown
          v-if="editingReeferDetails.isGensetRequired"
          v-model="editingReeferDetails.gensetApprovalChoice"
          textLabel="Genset approved by shipline"
          :options="gensetApprovalOptions"
          label="label"
          :reduce="(option) => option.value"
          :clearable="false"
          :components="{ Deselect: null }"
        ></FilterableDropdown>

        <div v-if="editingReeferDetails.isGensetRequired">
          <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Genset number</label>
          <input
            v-model="editingReeferDetails.gensetNumber"
            type="text"
            :maxlength="maxGensetNumberLength"
            placeholder="Not specified"
            class="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-uppercase tw-text-slate-900 tw-shadow-sm tw-shadow-slate-900/5 placeholder:tw-normal-case placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
          />
        </div>

        <!-- Temperature and vents apply to every reefer, also when it runs on in-house power -->
        <div>
          <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Temperature</label>
          <div class="tw-mt-1 tw-flex tw-items-center tw-gap-2">
            <input
              v-model="editingReeferDetails.gensetTemperatureValue"
              type="number"
              step="1"
              class="tw-w-28 tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 tw-shadow-sm tw-shadow-slate-900/5 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
              :class="{ 'tw-border-rose-400': !getIsTemperatureInRange() }"
            />
            <Button 
              v-for="unit in ['F', 'C']" 
              @click="editingReeferDetails.gensetTemperatureUnit = unit"
              :isPrimary="editingReeferDetails.gensetTemperatureUnit === unit"
              :isSecondary="editingReeferDetails.gensetTemperatureUnit !== unit">{{ unit }}</Button>
            
          </div>
          <div v-if="convertedTemperatureHint" class="tw-text-xs tw-text-slate-500">{{ convertedTemperatureHint }}</div>
          <p v-if="!getIsTemperatureInRange()" class="tw-mt-1 tw-text-xs tw-text-rose-600">
            Must be between -70°C and 30°C (-94°F and 86°F).
          </p>
        </div>

        <div>
          <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Vents open</label>
          <div class="tw-mt-1 tw-flex tw-items-center tw-gap-2">
            <input
              v-model="editingReeferDetails.gensetVentsOpenPercent"
              type="number"
              min="0"
              max="100"
              step="5"
              placeholder="Not specified"
              class="tw-w-32 tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 tw-shadow-sm tw-shadow-slate-900/5 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
              :class="{ 'tw-border-rose-400': !getIsVentsOpenPercentInRange() }"
            />
            <span class="tw-text-sm tw-text-slate-500">%</span>
          </div>
          <p v-if="!getIsVentsOpenPercentInRange()" class="tw-mt-1 tw-text-xs tw-text-rose-600">
            Must be a whole number between 0 (closed) and 100 (open).
          </p>
        </div>
      </div>

      <div class="tw-mt-6 tw-flex tw-items-center tw-justify-end tw-gap-2">
        <Button :isGreen="true" :enableLoading="isSaving" :disabled="isSaving || !getIsTemperatureInRange() || !getIsVentsOpenPercentInRange()" :isPrimary="true" @click="saveReeferDetails"
          icon="check" title="Save reefer details" aria-label="Save reefer details">Save</Button>
        <Button :isRed="true" :isSecondary="true" @click="$emit('update:isOpen', false)"
          title="Cancel edit reefer details" aria-label="Cancel edit reefer details">Cancel</Button>
      </div>
    </template>
  </Modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import Modal from '@/components/core/Modal/Modal.vue';
import FilterableDropdown from '@/components/core/FilterableDropdown.vue';
import Button from '@/components/core/Button.vue';
import { pushToast } from '@/lib/toast';

import UpdateContainerReeferDetailsMutation from '@/gql/containers/mutations/UpdateContainerReeferDetails.gql';

// Mirrors the backend guard in ops/container/genset_temperature.py
const MIN_GENSET_TEMPERATURE_C = -70;
const MAX_GENSET_TEMPERATURE_C = 30;
// Mirrors the backend guard in ops/container/genset_vents.py
const MIN_GENSET_VENTS_OPEN_PERCENT = 0;
const MAX_GENSET_VENTS_OPEN_PERCENT = 100;
// Mirrors the backend limit in ops/container/genset_number.py
const MAX_GENSET_NUMBER_LENGTH = 20;

// vue-select can't hold null as a selected value, so the nullable boolean goes through a string.
type GensetApprovalChoice = 'unknown' | 'no' | 'yes';

function convertToCelsius(value: number, unit: string): number {
  return unit === 'C' ? value : (value - 32) * 5 / 9;
}

function convertToFahrenheit(value: number, unit: string): number {
  return unit === 'F' ? value : value * 9 / 5 + 32;
}

function getGensetApprovalChoice(isApproved?: boolean | null): GensetApprovalChoice {
  if (isApproved === true) return 'yes';
  if (isApproved === false) return 'no';
  return 'unknown';
}

function getIsGensetApprovedFromChoice(choice: GensetApprovalChoice): boolean | null {
  if (choice === 'yes') return true;
  if (choice === 'no') return false;
  return null;
}

export default defineComponent({
  name: 'ContainerReeferDetailsEditModal',
  components: {
    Modal,
    FilterableDropdown,
    Button,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    // Needs id + the reeferDetails fragment fields
    container: {
      type: Object,
      default: null,
    },
  },
  emits: ['update:isOpen', 'saved'],
  data() {
    return {
      isSaving: false,
      maxGensetNumberLength: MAX_GENSET_NUMBER_LENGTH,
      editingReeferDetails: {
        isGensetRequired: false,
        gensetApprovalChoice: 'unknown' as GensetApprovalChoice,
        gensetNumber: '',
        gensetTemperatureValue: '' as string | number,
        gensetTemperatureUnit: 'F',
        gensetVentsOpenPercent: '' as string | number,
      },
      gensetApprovalOptions: [
        { value: 'unknown', label: 'Not set' },
        { value: 'no', label: 'No' },
        { value: 'yes', label: 'Yes' },
      ],
      gensetTemperatureUnits: [
        { value: 'F', label: '°F' },
        { value: 'C', label: '°C' },
      ],
    };
  },
  watch: {
    isOpen: {
      immediate: true,
      handler(isOpen: boolean) {
        if (isOpen) (this as any).resetEditingReeferDetails();
      },
    },
  },
  computed: {
    enteredTemperature(): number | null {
      const value = (this as any).editingReeferDetails.gensetTemperatureValue;
      if (value === '' || value === null || value === undefined) return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    },
    convertedTemperatureHint(): string {
      const vm = this as any;
      if (vm.enteredTemperature === null) return '';
      const unit = vm.editingReeferDetails.gensetTemperatureUnit;
      if (unit === 'C') {
        return `${convertToFahrenheit(vm.enteredTemperature, unit).toFixed(1)} °F`;
      }
      return `${convertToCelsius(vm.enteredTemperature, unit).toFixed(1)} °C`;
    },
    enteredVentsOpenPercent(): number | null {
      const value = (this as any).editingReeferDetails.gensetVentsOpenPercent;
      if (value === '' || value === null || value === undefined) return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    },
  },
  methods: {
    resetEditingReeferDetails() {
      const vm = this as any;
      const container = vm.container || {};
      const savedUnit = container.gensetTemperatureUnit || 'F';
      const savedValue = savedUnit === 'C' ? container.gensetTemperatureC : container.gensetTemperatureF;

      vm.editingReeferDetails = {
        isGensetRequired: !!container.isGensetRequired,
        gensetApprovalChoice: getGensetApprovalChoice(container.isGensetApprovedByShipline),
        gensetNumber: container.gensetNumber ?? '',
        gensetTemperatureValue: savedValue ?? '',
        gensetTemperatureUnit: savedUnit,
        gensetVentsOpenPercent: container.gensetVentsOpenPercent ?? '',
      };
    },
    getIsTemperatureInRange(): boolean {
      const vm = this as any;
      if (vm.enteredTemperature === null) return true;
      const celsius = convertToCelsius(vm.enteredTemperature, vm.editingReeferDetails.gensetTemperatureUnit);
      return celsius >= MIN_GENSET_TEMPERATURE_C && celsius <= MAX_GENSET_TEMPERATURE_C;
    },
    getIsVentsOpenPercentInRange(): boolean {
      const vm = this as any;
      // Empty means "not specified", which is allowed
      if (vm.enteredVentsOpenPercent === null) return true;
      return Number.isInteger(vm.enteredVentsOpenPercent)
        && vm.enteredVentsOpenPercent >= MIN_GENSET_VENTS_OPEN_PERCENT
        && vm.enteredVentsOpenPercent <= MAX_GENSET_VENTS_OPEN_PERCENT;
    },
    saveReeferDetails() {
      const vm = this as any;
      if (!vm.container?.id || !vm.getIsTemperatureInRange() || !vm.getIsVentsOpenPercentInRange()) return;

      vm.isSaving = true;
      vm.$apollo
        .mutate({
          mutation: UpdateContainerReeferDetailsMutation,
          variables: {
            containerId: vm.container.id,
            isGensetRequired: vm.editingReeferDetails.isGensetRequired,
            isGensetApprovedByShipline: getIsGensetApprovedFromChoice(vm.editingReeferDetails.gensetApprovalChoice),
            // Backend trims + uppercases; blank clears
            gensetNumber: vm.editingReeferDetails.gensetNumber,
            gensetTemperatureValue: vm.enteredTemperature,
            gensetTemperatureUnit: vm.enteredTemperature === null ? null : vm.editingReeferDetails.gensetTemperatureUnit,
            gensetVentsOpenPercent: vm.enteredVentsOpenPercent,
          },
        })
        .then((result) => {
          vm.isSaving = false;
          pushToast('Reefer details saved', 'success', 3000);
          vm.$emit('saved', result?.data?.updateContainerReeferDetails?.container);
          vm.$emit('update:isOpen', false);
        })
        .catch((error) => {
          vm.isSaving = false;
          const message = error?.graphQLErrors?.[0]?.message || 'Could not save reefer details';
          pushToast(message, 'error', 5000);
        });
    },
  },
});
</script>
